import { defineStore } from 'pinia';
import { ref } from 'vue';

import { trackAnalyticsEvent } from '@/utils/analytics/track';

export type ErrorType = 'network' | 'settings' | 'general';

export interface ErrorMessage {
  type: ErrorType;
  title: string;
  message: string;
  actionText: string;
  action: () => void;
  showRetry?: boolean;
}

export const useErrorStore = defineStore('error', () => {
  const isVisible = ref(false);
  const currentError = ref<ErrorMessage | null>(null);

  function truncateText(text: string, maxLen: number) {
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen);
  }

  function showError(error: ErrorMessage) {
    currentError.value = error;
    isVisible.value = true;

    // Emit analytics so we can correlate real, user-visible failures with route + breadcrumbs
    trackAnalyticsEvent({
      type: 'error_screen_shown',
      target: 'error_screen',
      timestamp: Date.now(),
      metadata: {
        errorType: error.type,
        title: truncateText(error.title, 120),
        message: truncateText(error.message, 500),
        actionText: truncateText(error.actionText, 60),
        showRetry: !!error.showRetry,
      },
    });
  }

  function hideError() {
    currentError.value = null;
    isVisible.value = false;
  }

  function showNetworkError(customMessage?: string, customAction?: () => void) {
    const defaultAction = () => location.reload();
    showError({
      type: 'network',
      title: 'Connection Problem',
      message:
        customMessage ||
        "We're having trouble connecting to our servers. Please check your internet connection and try again.",
      actionText: 'Reload Game',
      action: customAction || defaultAction,
      showRetry: true,
    });
  }

  function showSettingsError(
    customMessage?: string,
    customAction?: () => void
  ) {
    const defaultAction = () => location.reload();
    showError({
      type: 'settings',
      title: 'Game Loading Error',
      message:
        customMessage ||
        "We couldn't load the game settings. Don't worry - let's try loading the game again!",
      actionText: 'Try Again',
      action: customAction || defaultAction,
      showRetry: true,
    });
  }

  function showGeneralError(customMessage?: string, customAction?: () => void) {
    const defaultAction = () => location.reload();
    showError({
      type: 'general',
      title: 'Oops!',
      message:
        customMessage ||
        "Something unexpected happened. Let's get you back to playing!",
      actionText: 'Continue',
      action: customAction || defaultAction,
      showRetry: false,
    });
  }

  return {
    // State
    isVisible,
    currentError,

    // Actions
    showError,
    hideError,
    showNetworkError,
    showSettingsError,
    showGeneralError,
  };
});
